import {
  getCourseRequestDetail,
  updateCourseRequestStatus,
  useAppDispatch,
  useAppSelector,
} from '@redux'
import { Typography } from 'antd'
import { Controller, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Button, TextArea } from 'src/common'
import { AppModal } from 'src/components/AppModal'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { toast } from 'react-toastify'
import { ECourseRequestStatus } from 'src/interfaces/course-request-management'
import { REASON_CHARACTER_LIMITATION } from './config'

interface IProps {
  open: boolean
  onClose: () => void
}

interface IFormData {
  reason: string
}

export default function RejectCourseRequestDialog({ open, onClose }: IProps) {
  const { t } = useTranslation(['common', 'staffAndRole', 'teacherRequest'])
  const dispatch = useAppDispatch()

  const { data: courseRequestDetailData } = useAppSelector(
    (state) => state.courseRequestManagement.courseRequestDetail
  )
  const { id: courseRequestId, course } = courseRequestDetailData || {}

  const schema = z.object({
    reason: z
      .string()
      .trim()
      .nonempty({
        message: t('teacherRequest:reasonIsRequired'),
      })
      .min(REASON_CHARACTER_LIMITATION.MIN_LENGTH, {
        message: t('teacherRequest:reason_min_length', {
          minLength: REASON_CHARACTER_LIMITATION.MIN_LENGTH,
        }),
      })
      .max(REASON_CHARACTER_LIMITATION.MAX_LENGTH, {
        message: t('teacherRequest:reason_max_length', {
          maxLength: REASON_CHARACTER_LIMITATION.MAX_LENGTH,
        }),
      }),
  })

  const methods = useForm<IFormData>({
    defaultValues: {
      reason: '',
    },
    resolver: zodResolver(schema),
    mode: 'onSubmit',
    reValidateMode: 'onChange',
  })

  const {
    control,
    handleSubmit,
    formState: { isSubmitting },
  } = methods

  const onSubmit = async (data: IFormData) => {
    try {
      await dispatch(
        updateCourseRequestStatus({
          courseRequestId: String(courseRequestId),
          payload: {
            reason: data?.reason,
            response: ECourseRequestStatus.REJECTED,
          },
        })
      ).unwrap()

      dispatch(getCourseRequestDetail(String(course?.id)))
      toast.success('Reject course creation request successfully!')
      onClose()
    } catch (error: any) {
      toast.error(
        error?.message ||
          error?.data?.message ||
          'Something went wrong. Please try again'
      )
    }
  }

  return (
    <AppModal open={open} onClose={onClose} contentClassName="!px-8 !py-6">
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="h-auto">
          <div className="flex items-center justify-center gap-4">
            <div>
              <h1 className="m-0 text-[24px]">
                Want to reject this Course creation request?
              </h1>
            </div>
          </div>
        </div>

        <div className="flex flex-col max-w-[400px] mt-4 mb-8 mx-auto">
          <Typography
            style={{ color: '#424955FF', textAlign: 'center', fontSize: 16 }}
          >
            Are you sure you want to reject this request?
          </Typography>

          <Typography
            style={{
              color: '#424955FF',
              textAlign: 'center',
              fontSize: 16,
            }}
          >
            Once you have rejected the request, an email will be sent directly
            to the teacher.
          </Typography>
        </div>

        <div className="mt-4">
          <Controller
            name={'reason'}
            control={control}
            render={({ field: { value, onChange }, fieldState: { error } }) => {
              return (
                <TextArea
                  placeholder="Describe reject reason"
                  name="reason"
                  required
                  onChange={(e) => {
                    onChange(e?.target?.value)
                  }}
                  value={value}
                  style={{
                    marginTop: 10,
                    height: 'fit-content',
                    minHeight: 150,
                  }}
                  errors={error?.message}
                  isGrid
                  containerClassName="mt-3"
                />
              )
            }}
          />
        </div>

        <div className="flex justify-center items-center my-2 mt-4 gap-4">
          <Button type="ghost" size="small" onClick={onClose}>
            Check Again
          </Button>

          <Button
            type="primary"
            size="small"
            htmlType="submit"
            loading={isSubmitting}
            style={{
              color: '#ffffff',
              backgroundColor: '#ff0000',
              borderColor: '#ff0000',
            }}
          >
            Reject Request
          </Button>
        </div>
      </form>
    </AppModal>
  )
}
